import {
  combineLatest,
  firstValueFrom,
  fromEvent,
  interval,
  Subject,
  switchMap,
} from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { merge } from 'lodash';

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  DisplaySettingsResult,
  RecognizerType,
  SubtitleBoxSettings,
  WebsocketEvents,
  WebsocketService,
  WebsocketState,
} from '@app/services/websocket.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { hexToRgba } from '@app/shared/color';
import { License } from '@app/modules/license/license.types';
import { Location } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Message } from '@app/shared/models/app.model';
import { SessionService } from '@app/services/session.service';
import { SpeakseeOrderService } from '@app/modules/license/license.service';
import { SpeechService } from '@app/services/speech.service';

import { StableTextService } from '@app/directives/stable-text/stable-text.service';
import { SubtitleBoxComponent } from './subtitle-box/subtitle-box.component';
import { ViewportMonitorService } from './viewport-monitor.service';

const default_subtitle_settings: SubtitleBoxSettings = {
  position: '0.01x0.75',
  size: '0.8x0.2',
  background_color: '#000000',
  background_color_opacity: 70,
  overlay_color: '#000001',
  overlay_color_opacity: 1,
  max_visible_lines: 2,
  font_size: 32,
  font_color: '#FFFFFF',
  font_color_opacity: 100,
  clear_transcripts_after_seconds: 0,
};

const TRANSPARENT_OUTPUT_CLASS = 'is-transparent-output';

@Component({
  selector: 'app-av-view',
  standalone: true,
  imports: [
    SubtitleBoxComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
  templateUrl: './av-view.component.html',
  styleUrls: ['./av-view.component.scss'],
  providers: [StableTextService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.transparent-output]': 'transparentOutput()',
  },
})
export class AvViewComponent implements OnInit, OnDestroy {
  isLoading = signal<boolean>(true);
  hasAuthError = signal<boolean>(false);
  private readonly transparentRequested: boolean;
  /** Subtitles with nothing behind them, for a media server to composite as a layer. */
  readonly transparentOutput = computed(
    () =>
      this.transparentRequested ||
      this.currentSubtitleSettings().background_color_opacity <= 0
  );
  backgroundColor = computed(() => {
    if (this.transparentOutput()) {
      return 'transparent';
    }

    const settings = this.currentSubtitleSettings();
    return hexToRgba(
      settings.background_color,
      settings.background_color_opacity
    );
  });
  displaySettings = signal<DisplaySettingsResult | null>(null);
  currentSubtitleSettings = signal<SubtitleBoxSettings>(
    default_subtitle_settings
  );
  activeOutputView = signal<string>('overlay');

  public messages = signal<Message[]>([]);
  public outputLanguage = signal<string | null>(null);
  public recognizerType = signal<RecognizerType>(RecognizerType.TRANSLATION);
  public recognitionLanguage = signal<string | null>(null);
  public transcriptLanguage = computed(() =>
    this.recognizerType() === RecognizerType.TRANSCRIPTION
      ? this.recognitionLanguage()
      : this.outputLanguage()
  );
  public isTranslation = computed(() =>
    this.recognizerType() === RecognizerType.TRANSLATION
  );
  private translationBufferMs = signal<number>(0);
  public shouldApplyTranslationDelay = computed(() =>
    this.isTranslation() && !!this.translationBufferMs()
  );

  targetWidth = signal<number | null>(null);
  targetHeight = signal<number | null>(null);
  scaleFactor = signal<number>(1);
  viewportStyles = computed(() => {
    const width = this.targetWidth();
    const height = this.targetHeight();
    const scale = this.scaleFactor();

    if (!width || !height) {
      return {};
    }

    return {
      width: `${width * scale}px`,
      height: `${height * scale}px`,
    };
  });
  private lastClearedAt = signal<number>(0);

  enableEdit = false;
  deviceId: string;
  display: string;
  kitLicenses: License[];
  allLicenses: License[];
  private session: License;
  private licensesToListenToSignal: WritableSignal<License[]> = signal<
    License[]
  >([]);

  constructor(
    private speechService: SpeechService,
    private route: ActivatedRoute,
    private sessionService: SessionService,
    private orderService: SpeakseeOrderService,
    private destroyRef: DestroyRef,
    private websocketService: WebsocketService,
    private location: Location,
    private viewportMonitor: ViewportMonitorService,
    private stableTextService: StableTextService
  ) {
    this.enableEdit = this.route.snapshot.queryParamMap.get('edit') === 'true';
    this.transparentRequested =
      this.route.snapshot.queryParamMap.get('transparent') === 'true';

    toObservable(this.transparentOutput)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((transparent) =>
        document.documentElement.classList.toggle(
          TRANSPARENT_OUTPUT_CLASS,
          transparent
        )
      );

    // Get resolved data from the route
    const resolvedData = this.route.snapshot.data.resolvedData;
    this.session = resolvedData?.session || this.route.snapshot.data.session;
    this.deviceId = resolvedData?.deviceId || this.route.snapshot.params['id'];
    this.display =
      resolvedData?.display ||
      this.route.snapshot.queryParamMap.get('display') ||
      'HDMI-1';

    const widthParam = this.route.snapshot.queryParamMap.get('width');
    const heightParam = this.route.snapshot.queryParamMap.get('height');

    if (widthParam && heightParam) {
      const width = parseInt(widthParam, 10);
      const height = parseInt(heightParam, 10);

      if (!isNaN(width) && !isNaN(height) && width > 0 && height > 0) {
        this.targetWidth.set(width);
        this.targetHeight.set(height);
        this.viewportMonitor.setTargetResolution(width, height);
      }
    }

    this.allLicenses = this.orderService.licenses;
    this.kitLicenses = this.allLicenses.filter(
      (license) => license.type === 'microphonekit'
    );

    // Initialize licensesToListenToSignal with the current session
    if (this.session) {
      this.licensesToListenToSignal.set([this.session]);
    }

    // Initialize messages observable with speech service listener
    combineLatest([
      toObservable(this.transcriptLanguage).pipe(
        filter((lang): lang is string => lang !== null)
      ),
      toObservable(this.licensesToListenToSignal),
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(([language, licenses]) => {
          return this.speechService
            .startListener(licenses, language)
            .pipe(takeUntilDestroyed(this.destroyRef));
        })
      )
      .subscribe((messages) => {
        const clearedAt = this.lastClearedAt();
        if (clearedAt > 0) {
          const filteredMessages = messages.filter(m =>
            new Date(m.updated_at).getTime() > clearedAt
          );
          this.messages.set(filteredMessages);
        } else {
          this.messages.set(messages);
        }
      });
  }

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Sets up the speech listener and subscribes to live WebSocket updates.
   */
  async ngOnInit(): Promise<void> {
    // Clean URL if token was present
    const resolvedData = this.route.snapshot.data.resolvedData;
    if (resolvedData?.shouldCleanUrl) {
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.delete('token');

      const cleanUrl = `${window.location.pathname}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      this.location.replaceState(cleanUrl);
    }

    // Setup viewport change detection for the main component
    this.setupViewportMonitoring();

    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const currentMessages = this.messages();
        const ttlSeconds = this.currentSubtitleSettings().clear_transcripts_after_seconds || 0;

        if (!ttlSeconds || ttlSeconds <= 0 || currentMessages.length === 0) {
          return;
        }

        const latestMessageTime = Math.max(
          ...currentMessages.map(m => new Date(m.updated_at).getTime())
        );

        const now = Date.now();
        const ttlMilliseconds = ttlSeconds * 1000;
        const timeSinceLastMessage = now - latestMessageTime;

        if (timeSinceLastMessage > ttlMilliseconds) {
          this.lastClearedAt.set(Date.now());
          this.messages.set([]);
        }
      });

    // Prepare licenses to listen to for speech recognition
    const autoCaptionLicense = this.allLicenses.find(
      (license) => license.type === 'autocaption'
    );

    // If current session is a microphone kit and auto-caption license is available
    if (this.session.type === 'microphonekit' && autoCaptionLicense) {
      // Start session for auto-caption
      await firstValueFrom(
        this.sessionService.startSession(autoCaptionLicense)
      );
      this.licensesToListenToSignal.update((licenses) => [
        ...licenses,
        autoCaptionLicense,
      ]);
    }

    // WebSocket connection is already initialized in the resolver

    // Initialize messages observable with speech service listener
    this.websocketService.incomingMessages
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((message) => {
        switch (message.event) {
          case WebsocketEvents.SPEECH_SDK_RECOGNIZER_TYPE_RESULT:
          case WebsocketEvents.SPEECH_SDK_RECOGNIZER_TYPE_UPDATED: {
            const recognizerType = message.payload as RecognizerType;
            if (recognizerType) {
              this.recognizerType.set(recognizerType);
            }
            break;
          }
          case WebsocketEvents.SPEECH_SDK_RECOGNITION_LANGUAGE_RESULT: {
            const recognitionLanguages = message.payload as string[];
            if (recognitionLanguages && recognitionLanguages.length > 0) {
              this.recognitionLanguage.set(recognitionLanguages[0]);
            }
            break;
          }
          case WebsocketEvents.DISPLAY_SETTINGS_TARGET_LANGUAGES_UPDATE(
            this.display,
            this.activeOutputView()
          ): {
            // update output language on change
            const targetLanguages = message.payload as string[];
            if (targetLanguages && targetLanguages.length > 0) {
              this.outputLanguage.set(targetLanguages[0]);
            }
            break;
          }
          case WebsocketEvents.DISPLAY_SETTINGS_SUBTITLEBOX_UPDATED(
            this.display,
            this.activeOutputView()
          ): {
            const subtitleSettings = message.payload as SubtitleBoxSettings;
            this.currentSubtitleSettings.set(subtitleSettings);
            break;
          }
          case WebsocketEvents.DISPLAY_SETTINGS_RESULT(this.display): {
            const displaySettings = message.payload as DisplaySettingsResult;
            this.displaySettings.set(displaySettings);

            // Get the active output view
            const activeView = displaySettings.configuration.active_output_view;
            this.activeOutputView.set(activeView);

            // Extract settings based on active output view
            const viewSettings =
              displaySettings.configuration.output_views[activeView];

            if (
              viewSettings &&
              'appearance' in viewSettings &&
              viewSettings.appearance?.subtitlebox
            ) {
              const subtitleSettings = viewSettings.appearance.subtitlebox;
              this.currentSubtitleSettings.set(subtitleSettings);

              // Set output language if available
              if (
                'target_languages' in viewSettings &&
                viewSettings.target_languages?.length > 0
              ) {
                const targetLanguage = viewSettings.target_languages[0];
                if (targetLanguage) {
                  this.outputLanguage.set(targetLanguage);
                }
              }
            }

            // Apply text behavior (translation buffer delay)
            if (
              viewSettings &&
              'text_behavior' in viewSettings
            ) {
              const bufferMs = viewSettings.text_behavior?.translation_buffer_ms;
              this.translationBufferMs.set(bufferMs || 0);
              this.stableTextService.setTranslationBufferMs(bufferMs || 0);
            }

            this.isLoading.set(false);
            break;
          }
          case WebsocketEvents.DISPLAY_SETTINGS_TEXT_BEHAVIOR_UPDATED(
            this.display,
            this.activeOutputView()
          ): {
            const translationBufferMs = message.payload?.translation_buffer_ms;
            this.translationBufferMs.set(translationBufferMs || 0);
            this.stableTextService.setTranslationBufferMs(translationBufferMs || 0);
            break;
          }
          default: {
            // Handle subtitlebox_updated events that might not match the exact format
            if (message.event?.includes('subtitlebox_updated')) {
              const subtitleSettings = message.payload as SubtitleBoxSettings;
              if (subtitleSettings?.position && subtitleSettings?.size) {
                this.currentSubtitleSettings.set(subtitleSettings);
              }
            }
            break;
          }
        }
      });

    // retrieve settings on load for first time
    this.websocketService.state$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state === WebsocketState.OPEN) {
          this.hasAuthError.set(false);
          this.websocketService.outgoingMessageSubject.next({
            event: WebsocketEvents.DISPLAY_SETTINGS_GET(this.display),
            payload: {
              id: this.deviceId, // Use the actual device UUID
            },
          });
          // Request recognizer type and recognition language for RTL direction
          this.websocketService.outgoingMessageSubject.next({
            event: WebsocketEvents.SPEECH_SDK_RECOGNIZER_TYPE_GET,
            payload: {
              id: this.deviceId,
            },
          });
          this.websocketService.outgoingMessageSubject.next({
            event: WebsocketEvents.SPEECH_SDK_RECOGNITION_LANGUAGE_GET,
            payload: {
              id: this.deviceId,
            },
          });
        }
      });

    // Listen for JWT/auth failure (close code 1008)
    this.websocketService.lastCloseCode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((code) => {
        if (code === 1008) {
          this.hasAuthError.set(true);
          this.isLoading.set(false);
        }
      });
  }


  /**
   * Sets up monitoring for viewport changes to ensure proper rendering
   * whether in iframe or standalone window
   */
  private setupViewportMonitoring(): void {
    // Monitor for iframe resizing via postMessage (if embedded)
    fromEvent<MessageEvent>(window, 'message')
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe((event) => {
        if (
          event.data?.type === 'viewport-resize' ||
          event.data?.type === 'iframe-resize'
        ) {
          // Force recalculation when parent notifies of resize
          this.recalculateScale();
        }
      });

    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe(() => {
        this.recalculateScale();
      });

    // Detect if we're in an iframe and setup communication
    if (window.self !== window.top) {
      // We're in an iframe - notify parent we're ready
      window.parent.postMessage({ type: 'av-view-ready' }, '*');
    }

    // Monitor document visibility changes
    fromEvent(document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!document.hidden) {
          // Reapply settings when becoming visible
          this.recalculateScale();
        }
      });

    // Initial scale calculation
    this.recalculateScale();
  }

  /**
   * Recalculates scale factor based on current window size and target resolution
   */
  private recalculateScale(): void {
    const targetWidth = this.targetWidth();
    const targetHeight = this.targetHeight();

    if (!targetWidth || !targetHeight) {
      return;
    }

    const availableWidth = window.innerWidth;
    const availableHeight = window.innerHeight;

    const scaleX = availableWidth / targetWidth;
    const scaleY = availableHeight / targetHeight;
    const scale = Math.min(scaleX, scaleY);

    this.scaleFactor.set(scale);
  }

  /**
   * Handler for color change input.
   * Updates the background color over WebSocket and in local state.
   *
   * @param event - The input event containing the selected color
   */
  setColor(event: any): void {
    if (!this.enableEdit) {
      return;
    }

    const background_color = event.target.value;
    this.currentSubtitleSettings.update((settings) => ({
      ...settings,
      background_color,
    }));

    this.updateSubtitleBoxSetting({ background_color });
  }

  /**
   * Updates the subtitle box settings in the backend.
   * @param settingChanges - Partial settings to update
   */
  private updateSubtitleBoxSetting(
    settingChanges: Partial<SubtitleBoxSettings>
  ): void {
    const currentSettings = this.currentSubtitleSettings();
    const updatedSettings = merge({ ...currentSettings }, settingChanges);

    // Use the dynamic event with the current display and active output view
    this.websocketService.outgoingMessageSubject.next({
      event: WebsocketEvents.DISPLAY_SETTINGS_SUBTITLEBOX_UPDATE(
        this.display,
        this.activeOutputView()
      ),
      payload: {
        id: this.deviceId,
        data: updatedSettings,
      },
    });
  }

  /**
   * Updates the subtitle box settings when changed by the user.
   *
   * @param settings - The updated subtitle box settings
   */
  updateSubtitleBox(settings: SubtitleBoxSettings): void {
    if (!this.enableEdit) {
      return;
    }

    // Update local state first for immediate UI response
    this.currentSubtitleSettings.set(settings);

    // Send the update to the websocket
    this.updateSubtitleBoxSetting(settings);
  }

  ngOnDestroy(): void {
    document.documentElement.classList.remove(TRANSPARENT_OUTPUT_CLASS);

    if (this.deviceId) {
      sessionStorage.removeItem(`av-view-token-${this.deviceId}`);
    }

    if (this.websocketService) {
      this.websocketService.disconnect();
    }
  }
}
